import { Link } from "react-router-dom";
import styled from "styled-components";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";
import { Button } from "../../components/styles/Button";
import { size } from "../../components/styles/Breakpoints";

import useSeo from "../../hooks/useSeo";
import { ORIGIN, SITE_NAME } from "../../data/seo";
import { AREAS } from "../../data/areaPages";
import { PRODUCT_ORIGIN, BRANDS, brandForHost } from "../../data/brand";

/*
 * Every unmatched URL previously rendered nothing at all - the <Switch> in
 * App.js simply ran out of routes and returned an empty page, served with
 * HTTP 200. That is a soft 404: a search engine sees a successful response
 * for /wp-admin, /typo, and any other address anyone ever links to by
 * mistake, and can index unlimited junk.
 *
 * A client-rendered app cannot set an HTTP status, so `noindex` is the
 * mechanism that actually keeps these out of the index. nginx returns a real
 * 404 for anything that looks like a file (see CLAUDE.md section 6a); this
 * page covers the routes the app itself owns.
 */
const Wrap = styled.div`
    padding: 4rem 0 5rem 0;
    max-width: 44rem;
    @media screen and (max-width: ${size.sm}px) {
        padding: 2.5rem 0 3rem 0;
    }
`;

const Code = styled.div`
    font-size: 0.8rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${(props) => props.theme.grey};
    margin-bottom: 0.75rem;
`;

const Title = styled.h1`
    margin: 0 0 1rem 0;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: 1px;
    color: ${(props) => props.theme.darkblue};
`;

const Lede = styled.p`
    margin: 0 0 2rem 0;
    line-height: 1.6;
`;

const Actions = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 3rem;
`;

const SubTitle = styled.h2`
    font-size: 1.1rem;
    font-weight: 400;
    line-height: normal;
    color: ${(props) => props.theme.darkblue};
    border-bottom: 1px solid ${(props) => props.theme.lightgrey};
    padding-bottom: 0.5rem;
    margin: 0 0 1rem 0;
`;

const Links = styled.div`
    font-size: 0.9rem;
    & a {
        display: inline-block;
        margin: 0 1.1rem 0.5rem 0;
        color: ${(props) => props.theme.darkblue};
    }
    & a:hover {
        text-decoration: underline;
    }
`;

export default function NotFound() {
    /*
     * WHICH SITE IS THIS? The two brands share one build and one nginx, and
     * everything that decides between them goes through brand.js - see
     * CLAUDE.md section 6f.
     *
     * This page did not, and it showed. On doorstepdata.co.uk it drew the
     * agency NavBar, the agency Footer, a "Contact us" button and fourteen
     * "Areas we cover" links - every one of which is an agency route that
     * answers 404 on the product domain. So the product's not-found page
     * was itself a page of broken links, under another company's name.
     *
     * Found on 29 Oct 2026 by a PayPal return_url landing on
     * /account/payment-complete, which matches no route.
     */
    /*
     * `.key`, NOT the object. A brand is a record, brandForHost returns one
     * of two shared instances, and ProductChrome compares the key - so this
     * follows it rather than inventing a second idiom. Comparing against a
     * BRANDS.PRODUCT that does not exist (it is lower case) silently
     * evaluated to false and left the agency body on the product site, with
     * the chrome around it already correct. A wrong brand check fails quietly
     * in exactly one direction: everything looks like the agency.
     */
    const product = brandForHost(window.location.hostname).key === "product";
    const name = product ? BRANDS.product.name : SITE_NAME;
    const origin = product ? PRODUCT_ORIGIN : ORIGIN;

    useSeo({
        title: "Page not found | " + name,
        description: product
            ? "That page could not be found. Look up a postcode or an address instead."
            : "That page could not be found. Browse our properties or get in touch.",
        canonical: origin + window.location.pathname,
        noindex: true,
    });

    return (
        <>
            <SiteNav location="public" />
            <Container>
                <Wrap>
                    <Code>Error 404</Code>
                    <Title>We can't find that page</Title>
                    <Lede>
                        {product
                            ? "The address may be mistyped. Every postcode and address in England and Wales is a lookup away."
                            : "The address may be mistyped, or the property may have been let or sold and taken down. Everything we currently have is a click away."}
                    </Lede>

                    <Actions>
                        {product ? (
                            <>
                                <Link to="/">
                                    <Button bright type="button">
                                        Look up a postcode
                                    </Button>
                                </Link>
                                <Link to="/house-prices">
                                    <Button primary type="button">
                                        House prices
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link to="/">
                                    <Button bright type="button">
                                        See all properties
                                    </Button>
                                </Link>
                                <Link to="/contact">
                                    <Button primary type="button">
                                        Contact us
                                    </Button>
                                </Link>
                            </>
                        )}
                    </Actions>

                    {/*
                      * AGENCY ONLY. These are /estate-agents/<slug> routes,
                      * which the product's nginx block refuses with a 404 -
                      * so offering them here would be a not-found page whose
                      * every link is also not found.
                      */}
                    {!product && (
                        <>
                            <SubTitle>Areas we cover</SubTitle>
                            <Links>
                                {AREAS.map((a) => (
                                    <Link key={a.slug} to={`/estate-agents/${a.slug}`}>
                                        {a.name}
                                    </Link>
                                ))}
                            </Links>
                        </>
                    )}
                </Wrap>
            </Container>
            <SiteFooter />
        </>
    );
}
